/**
 * useDviSession - Hook for managing DVI session state
 * 
 * Provides session creation, sharing, and approved job addition via edge functions.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAddonSuggestionTrigger } from '@/contexts/AddonSuggestionContext';
import { useToast } from '@/hooks/use-toast';

interface DviSession {
  id: string;
  status: 'draft' | 'shared' | 'customer_viewed' | 'customer_responded' | 'archived';
  // Phase 1A: customer_view_token no longer selected from DB (hashed at rest)
  // Use status !== 'draft' to determine if session has been shared
  token_expires_at: string | null;
  created_at: string;
}

interface DviFindingWithDecision {
  id: string;
  customer_item_token: string;
  condition: 'green' | 'yellow' | 'red' | null;
  title: string | null;
  summary: string | null;
  customer_decision: 'none' | 'approved' | 'declined';
  customer_notes: string | null;
  recommended_jobs: Array<{ label: string; catalog_job_id: string; minutes: number }>;
  status: string;
  added_to_ticket_at: string | null;
}

interface ShareSessionResult {
  share_url: string;
  token: string;
  expires_at: string;
}

interface AddApprovedJobsResult {
  success: boolean;
  jobs_added: Array<{ job_id: string; label: string; minutes: number }>;
  total_minutes: number;
}

export function useDviSession(ticketId: string, shopId: string) {
  // CORE-BP item 6 — see the post-add evaluation in the add-jobs mutation.
  const { evaluateAfterWorkAdded } = useAddonSuggestionTrigger();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  // Fetch existing session for this ticket
  const { data: session, isLoading: isLoadingSession } = useQuery({
    queryKey: ['dvi-session', ticketId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_dvi_sessions')
        .select('id, status, token_expires_at, created_at')
        .eq('ticket_id', ticketId)
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as DviSession | null;
    },
    enabled: !!ticketId && !!shopId,
  });

  // Fetch findings with customer decisions
  const { data: findingsWithDecisions = [] } = useQuery({
    queryKey: ['dvi-findings-with-decisions', ticketId, session?.id],
    queryFn: async () => {
      if (!session?.id) return [];
      const { data, error } = await supabase
        .from('ticket_dvi_findings')
        .select('id, customer_item_token, condition, title, summary, customer_decision, customer_notes, recommended_jobs, status, added_to_ticket_at')
        .eq('session_id', session.id);
      if (error) throw error;
      return data as DviFindingWithDecision[];
    },
    enabled: !!session?.id,
  });

  // Create session mutation
  const createSessionMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('dvi-create-session', {
        body: { shop_id: shopId, ticket_id: ticketId },
      });
      if (error) throw error;
      return data as { session_id: string; status: string; is_new: boolean };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dvi-session', ticketId] });
      toast({
        title: data.is_new ? 'Session created' : 'Session found',
        description: data.is_new ? 'New DVI session created' : 'Using existing DVI session',
      });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to create session', description: error.message, variant: 'destructive' });
    },
  });

  // Share session mutation
  const shareSessionMutation = useMutation({
    mutationFn: async (expiresInHours?: number) => {
      if (!session?.id) throw new Error('No session to share');
      const { data, error } = await supabase.functions.invoke('dvi-share-session', {
        body: { session_id: session.id, expires_in_hours: expiresInHours || 168 },
      });
      if (error) throw error;
      return data as ShareSessionResult;
    },
    onSuccess: (data) => {
      setShareUrl(data.share_url);
      // Copy to clipboard
      navigator.clipboard.writeText(data.share_url).then(() => {
        toast({
          title: 'Link copied!',
          description: 'Customer link copied to clipboard. Expires in 7 days.',
        });
      });
      queryClient.invalidateQueries({ queryKey: ['dvi-session', ticketId] });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to share session', description: error.message, variant: 'destructive' });
    },
  });

  // Add approved jobs mutation
  const addApprovedJobsMutation = useMutation({
    mutationFn: async (itemTokens: string[]) => {
      if (!session?.id) throw new Error('No session');
      const { data, error } = await supabase.functions.invoke('dvi-add-approved-jobs', {
        body: { session_id: session.id, item_tokens: itemTokens },
      });
      if (error) throw error;
      return data as AddApprovedJobsResult;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['dvi-findings-with-decisions', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['dvi-findings', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast({
        title: 'Jobs added',
        description: `${data.jobs_added.length} job(s) added (${data.total_minutes} min)`,
      });

      // Rollover chain (re-planned from its job ledger) + cascade, as AddJobDialog does
      try {
        const { recomputeRolloverChainWork } = await import('@/utils/rolloverChain');
        const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');

// CSI-T1a: skip cascade entirely if the ticket was soft-deleted
        // between the dvi-add-approved-jobs call and this re-fetch.
        // `.maybeSingle()` returns null for the deleted-ticket case;
        // the existing `if (ticketForChain && ...)` guard then short-
        // circuits before any rollover/cascade write.
        const { data: ticketForChain } = await supabase
          .from('tickets')
          .select('parent_ticket_id, sequence_index, rollover_of_ticket_id, split_reason, assigned_tech_id, scheduled_date, predicted_minutes')
          .eq('id', ticketId)
          .is('deleted_at', null)
          .maybeSingle();

        // ROLLOVER CHAIN: dvi-add-approved-jobs re-planned the chain from its
        // job ledger server-side; asking again is idempotent.
        if (ticketForChain) {
          await recomputeRolloverChainWork(ticketId);
        }

        if (ticketForChain?.assigned_tech_id && ticketForChain?.scheduled_date) {
          await triggerBoardCascade(shopId, ticketForChain.scheduled_date, ticketForChain.assigned_tech_id);
        }
      } catch (chainError) {
        console.error('[useDviSession] Error propagating wall-time change:', chainError);
      }

      // CORE-BP item 6 — DVI-approved work joins an EXISTING ticket, so the
      // same question applies. Asked once, after the batch settled, and only
      // when jobs genuinely landed. This is not DVI-driven suggestion
      // intelligence: the evaluator sees the ticket's work, exactly as it does
      // for a hand-added job, and knows nothing about findings.
      if ((data.jobs_added?.length ?? 0) > 0) {
        await evaluateAfterWorkAdded(ticketId);
      }
    },
    onError: (error: any) => {
      toast({ title: 'Failed to add jobs', description: error.message, variant: 'destructive' });
    },
  });

  // Computed values
  const approvedFindings = findingsWithDecisions.filter(f => 
    f.customer_decision === 'approved' && !f.added_to_ticket_at
  );
  const declinedFindings = findingsWithDecisions.filter(f => f.customer_decision === 'declined');
  const pendingFindings = findingsWithDecisions.filter(f => f.customer_decision === 'none');

  return {
    session,
    isLoadingSession,
    findingsWithDecisions,
    approvedFindings,
    declinedFindings,
    pendingFindings,
    shareUrl,
    
    // Mutations
    createSession: () => createSessionMutation.mutateAsync(),
    isCreatingSession: createSessionMutation.isPending,
    
    shareSession: (expiresInHours?: number) => shareSessionMutation.mutateAsync(expiresInHours),
    isSharing: shareSessionMutation.isPending,
    
    addApprovedJobs: (itemTokens: string[]) => addApprovedJobsMutation.mutateAsync(itemTokens),
    isAddingJobs: addApprovedJobsMutation.isPending,
  };
}
